import type { SignaturePanelProps, MemberOption } from './signatureTypes';
import type { TriStateCheck } from './spoolDetailFormUi';

/** Common ids passed from `SpoolDetailModal` into workflow step panels for API payloads. */
export interface SpoolStepPanelContext {
  projectId?: number | string | null;
  pipeId?: number | string | null;
  /** Required for spoolsbase presigned document uploads (`getPresignedUrlToUploadSpoolsbaseDoc`). */
  projectTypeId?: number | string | null;
  spoolId?: number | null;
  spoolName?: string | null;
  weldId?: string | null;
  weldNumber?: string | null;
}

export function triStateToApi(state: TriStateCheck): 'pass' | 'fail' | 'pending' {
  if (state === 'pass') return 'pass';
  if (state === 'fail') return 'fail';
  return 'pending';
}

/** File objects are not JSON-serializable; capture metadata for drafts / multipart uploads. */
export function fileListToMeta(files: File[]): { count: number; names: string[]; sizesBytes: number[] } {
  return {
    count: files.length,
    names: files.map((f) => f.name),
    sizesBytes: files.map((f) => f.size),
  };
}

export interface QcSignaturePayload {
  qcTeamMemberId: string | null;
  qcUserId: string | number | null;
  selectedName: string;
  selectedLabel: string;
  signerNameFromPad: string;
  signatureImage: string;
  stampImage: string;
  timestamp: string;
  signed: boolean;
  comment: string;
}

export interface WelderSignaturePayload {
  welderTeamMemberId: string | null;
  welderUserId: string | number | null;
  selectedName: string;
  selectedLabel: string;
  signerNameFromPad: string;
  signatureImage: string;
  stampImage: string;
  timestamp: string;
  signed: boolean;
  comment: string;
}

export interface CustomerRepSignaturePayload {
  customerRepTeamMemberId: string | null;
  customerRepUserId: string | number | null;
  selectedName: string;
  selectedLabel: string;
  signerNameFromPad: string;
  signatureImage: string;
  stampImage: string;
  timestamp: string;
  signed: boolean;
  comment: string;
}

export type CustomerRepSignatureBuildInput = {
  customerRepresentativeList: MemberOption[];
  selectedCustomerRepId: string | null;
  signatureData: { signature: Record<string, unknown> } | null | undefined;
};

export interface CoatingInspectorPayload {
  coatingTeamMemberId: string | null;
  coatingUserId: string | number | null;
  selectedName: string;
  selectedLabel: string;
  signerNameFromPad: string;
  signatureImage: string;
  stampImage: string;
  timestamp: string;
  signed: boolean;
  comment: string;
}

function findMemberByValue(list: MemberOption[], value: string | null): MemberOption | null {
  if (value == null || value === '') return null;
  return list.find((m) => m.value === value) ?? null;
}

function padString(rec: Record<string, unknown>, key: string): string {
  const v = rec[key];
  return typeof v === 'string' ? v : '';
}

export function getQcPadRecord(sig: SignaturePanelProps): Record<string, unknown> {
  const root = sig.qcSignatureData as Record<string, unknown> | null | undefined;
  if (!root) return {};
  const inner = root.signature as Record<string, unknown> | undefined;
  const qc = inner?.qc ?? root.qc;
  if (qc && typeof qc === 'object') return qc as Record<string, unknown>;
  return {};
}

export function getWelderPadRecord(sig: SignaturePanelProps): Record<string, unknown> {
  const root = sig.welderSignatureData as Record<string, unknown> | null | undefined;
  if (!root) return {};
  const inner = root.signature as Record<string, unknown> | undefined;
  const w = inner?.customer ?? inner?.welder ?? root.customer ?? root.welder;
  if (w && typeof w === 'object') return w as Record<string, unknown>;
  return {};
}

export function getCoatingPadRecord(sig: SignaturePanelProps): Record<string, unknown> {
  const root = sig.coatingSignatureData as Record<string, unknown> | null | undefined;
  if (!root) return {};
  const inner = root.signature as Record<string, unknown> | undefined;
  const c = inner?.customer ?? inner?.coating ?? root.customer;
  if (c && typeof c === 'object') return c as Record<string, unknown>;
  return {};
}

export function buildQcSignaturePayload(sig: SignaturePanelProps): QcSignaturePayload {
  const member = findMemberByValue(sig.qcList, sig.selectedQcId);
  const pad = getQcPadRecord(sig);
  const selectedName = (member?.name ?? member?.label ?? '').trim();
  const selectedLabel = (member?.label ?? member?.name ?? '').trim();
  const signerNameFromPad = padString(pad, 'name').trim();
  const signatureImage = padString(pad, 'signature');
  const stampImage = padString(pad, 'stampImage');
  const timestamp = padString(pad, 'timestamp');
  const comment = padString(pad, 'comment');
  return {
    qcTeamMemberId: sig.selectedQcId,
    qcUserId: member?.userId ?? null,
    selectedName,
    selectedLabel,
    signerNameFromPad,
    signatureImage,
    stampImage,
    timestamp,
    signed: signatureImage.length > 0,
    comment,
  };
}

function getCustomerRepPadRecord(input: CustomerRepSignatureBuildInput): Record<string, unknown> {
  const root = input.signatureData as Record<string, unknown> | null | undefined;
  if (!root) return {};
  const inner = root.signature as Record<string, unknown> | undefined;
  const c = inner?.customer ?? root.customer;
  if (c && typeof c === 'object') return c as Record<string, unknown>;
  return {};
}

export function buildCustomerRepSignaturePayload(
  input: CustomerRepSignatureBuildInput
): CustomerRepSignaturePayload {
  const member = findMemberByValue(input.customerRepresentativeList, input.selectedCustomerRepId);
  const pad = getCustomerRepPadRecord(input);
  const selectedName = (member?.name ?? member?.label ?? '').trim();
  const selectedLabel = (member?.label ?? member?.name ?? '').trim();
  const signerNameFromPad = padString(pad, 'name').trim();
  const signatureImage = padString(pad, 'signature');
  const stampImage = padString(pad, 'stampImage');
  const timestamp = padString(pad, 'timestamp');
  const comment = padString(pad, 'comment');
  return {
    customerRepTeamMemberId: input.selectedCustomerRepId,
    customerRepUserId: member?.userId ?? null,
    selectedName,
    selectedLabel,
    signerNameFromPad,
    signatureImage,
    stampImage,
    timestamp,
    signed: signatureImage.length > 0,
    comment,
  };
}

export function buildWelderSignaturePayload(sig: SignaturePanelProps): WelderSignaturePayload {
  const member = findMemberByValue(sig.welderList, sig.selectedWelderId);
  const pad = getWelderPadRecord(sig);
  const selectedName = (member?.name ?? member?.label ?? '').trim();
  const selectedLabel = (member?.label ?? member?.name ?? '').trim();
  const signerNameFromPad = padString(pad, 'name').trim();
  const signatureImage = padString(pad, 'signature');
  const stampImage = padString(pad, 'stampImage');
  const timestamp = padString(pad, 'timestamp');
  const comment = padString(pad, 'comment');
  return {
    welderTeamMemberId: sig.selectedWelderId,
    welderUserId: member?.userId ?? null,
    selectedName,
    selectedLabel,
    signerNameFromPad,
    signatureImage,
    stampImage,
    timestamp,
    signed: signatureImage.length > 0,
    comment,
  };
}

export function buildCoatingInspectorPayload(sig: SignaturePanelProps): CoatingInspectorPayload {
  const member = findMemberByValue(sig.coatingList, sig.selectedCoatingId);
  const pad = getCoatingPadRecord(sig);
  const selectedName = (member?.name ?? member?.label ?? '').trim();
  const selectedLabel = (member?.label ?? member?.name ?? '').trim();
  const signerNameFromPad = padString(pad, 'name').trim();
  const signatureImage = padString(pad, 'signature');
  const stampImage = padString(pad, 'stampImage');
  const timestamp = padString(pad, 'timestamp');
  const comment = padString(pad, 'comment');
  return {
    coatingTeamMemberId: sig.selectedCoatingId,
    coatingUserId: member?.userId ?? null,
    selectedName,
    selectedLabel,
    signerNameFromPad,
    signatureImage,
    stampImage,
    timestamp,
    signed: signatureImage.length > 0,
    comment,
  };
}

export function parseOptionalFloat(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

export function parseOptionalInt(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = parseInt(t, 10);
  return Number.isFinite(n) ? n : null;
}

/** Normalized shape for documents already stored on a weld-step payload. */
export interface SpoolStepPersistedDoc {
  fileName: string;
  s3Key: string;
  uploadedBy?: string;
  documentCategory?: string;
  tags?: string[];
  spoolDocId?: number;
  uploadedAt?: string;
}

function documentsArrayFromPayload(pl: Record<string, unknown>): unknown[] {
  const keys = [
    'documents',
    'documentList',
    'document_list',
    'attachmentDocuments',
    'attachment_documents',
    'spoolDocuments',
    'spool_documents',
  ] as const;
  for (const k of keys) {
    const v = pl[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

function coerceDocString(v: unknown): string {
  if (typeof v === 'string') return v.trim();
  if (v != null && v !== '') return String(v).trim();
  return '';
}

export function parseDocumentsFromStepPayload(
  pl: Record<string, unknown> | null | undefined
): SpoolStepPersistedDoc[] {
  if (!pl) return [];

  console.log('pl', pl);
  const raw = documentsArrayFromPayload(pl);
  const out: SpoolStepPersistedDoc[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const fileNameRaw =
      o.fileName ??
      o.file_name ??
      o.name ??
      o.originalFileName ??
      o.original_file_name;
    const s3KeyRaw = o.s3Key ?? o.s3_key ?? o.key ?? o.s3Path ?? o.s3_path;
    const fileName = coerceDocString(fileNameRaw);
    const s3Key = coerceDocString(s3KeyRaw);
    if (!fileName || !s3Key) continue;
    const uploadedBy = o.uploadedBy ?? o.uploaded_by;
    const documentCategory = o.documentCategory ?? o.document_category;
    const uploadedAtRaw = o.uploadedAt ?? o.uploaded_at ?? o.createdAt ?? o.created_at;
    const tags = Array.isArray(o.tags) ? o.tags.map(String) : undefined;
    let spoolDocId: number | undefined;
    const sid = o.spoolDocId ?? o.spool_doc_id;
    if (sid != null && !Number.isNaN(Number(sid))) spoolDocId = Number(sid);
    out.push({
      fileName,
      s3Key,
      ...(typeof uploadedBy === 'string' ? { uploadedBy } : {}),
      ...(typeof documentCategory === 'string' ? { documentCategory } : {}),
      ...(typeof uploadedAtRaw === 'string' && uploadedAtRaw.trim() !== ''
        ? { uploadedAt: uploadedAtRaw.trim() }
        : {}),
      ...(tags && tags.length > 0 ? { tags } : {}),
      ...(spoolDocId != null ? { spoolDocId } : {}),
    });
  }
  return out;
}

/** Parses API timestamps including `YYYY-MM-DD HH:mm:ss` without timezone. */
export function formatSpoolStepDocumentDate(input: string | null | undefined): string {
  if (input == null) return '';
  const s = String(input).trim();
  if (s === '') return '';
  let candidate = s;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) {
    candidate = `${s.slice(0, 10)}T${s.slice(11)}`;
  }
  const d = new Date(candidate);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '';
  }
}

export function mergeSpoolStepDocumentsByS3Key<T extends { s3Key: string }>(
  existing: T[],
  incoming: T[]
): T[] {
  const map = new Map<string, T>();
  for (const doc of existing) map.set(doc.s3Key, doc);
  for (const doc of incoming) map.set(doc.s3Key, doc);
  return Array.from(map.values());
}

/** Wire shape for weld-step save payloads (`uploadedBy` required for TS / API parity). */
export type SpoolStepDocumentWire = {
  fileName: string;
  s3Key: string;
  uploadedBy: string;
  documentCategory?: string;
  tags?: string[];
  spoolDocId?: number;
};

export function normalizeSpoolStepDocumentsForSave(
  docs: Array<{
    fileName: string;
    s3Key: string;
    uploadedBy?: string;
    documentCategory?: string;
    tags?: string[];
    spoolDocId?: number;
  }>
): SpoolStepDocumentWire[] {
  return docs.map((d) => ({
    fileName: d.fileName,
    s3Key: d.s3Key,
    uploadedBy: d.uploadedBy ?? '',
    ...(d.documentCategory ? { documentCategory: d.documentCategory } : {}),
    ...(d.tags && d.tags.length > 0 ? { tags: d.tags } : {}),
    ...(d.spoolDocId != null ? { spoolDocId: d.spoolDocId } : {}),
  }));
}
